import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanInput, StanModal } from '@/components';
import { type CreateInvoiceFormData, createInvoiceSchema } from '../validations/customerForm';

interface CreateInvoiceModalProps {
    isOpen: boolean;
    currencySymbol: string;
    onClose: () => void;
    onSubmit: (payload: { description: string; amount: number }) => void;
    isLoading?: boolean;
    serverErrors?: Record<string, string[]>;
}

export function CreateInvoiceModal({
    isOpen,
    currencySymbol,
    onClose,
    onSubmit,
    isLoading = false,
    serverErrors,
}: CreateInvoiceModalProps) {
    const form = useForm<CreateInvoiceFormData>({
        resolver: zodResolver(createInvoiceSchema),
        defaultValues: {
            description: '',
            amount: '',
        },
    });

    useEffect(() => {
        if (!isOpen) {
            form.reset();
            // Clear any server errors when modal closes
            form.clearErrors();
        }
    }, [isOpen, form]);

    // Set server-side validation errors on form fields
    useEffect(() => {
        if (serverErrors && isOpen) {
            Object.entries(serverErrors).forEach(([field, messages]) => {
                const fieldName = field as keyof CreateInvoiceFormData;
                if (messages && messages.length > 0) {
                    form.setError(fieldName, {
                        type: 'server',
                        message: messages[0], // Use first error message
                    });
                }
            });
        }
    }, [serverErrors, form, isOpen]);

    const handleSubmit = (data: CreateInvoiceFormData) => {
        onSubmit({
            description: data.description,
            amount: Number(data.amount),
        });
    };

    return (
        <StanModal
            id='create-invoice-modal'
            isOpen={isOpen}
            title='Send Invoice'
            primaryActionLabel='Send'
            primaryActionLoading={isLoading}
            onPrimaryAction={form.handleSubmit(handleSubmit)}
            onClose={onClose}
            aria-label='Create and send invoice'
        >
            <p className='text-grey caption-1-regular text-center'>
                Send a custom payment request, separate from existing subscriptions or payment plans.
            </p>
            <form onSubmit={form.handleSubmit(handleSubmit)} className='mt-6 space-y-4'>
                <Controller
                    name='description'
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <StanInput
                            id='invoice-description'
                            name='invoice-description'
                            topLabel='Description'
                            placeholder='e.g. My Fulfillment Order'
                            value={field.value}
                            onChange={field.onChange}
                            error={Boolean(fieldState.error)}
                            hintText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    name='amount'
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <StanInput
                            id='invoice-amount'
                            name='invoice-amount'
                            topLabel={`Amount (${currencySymbol})`}
                            placeholder='$9.99'
                            value={field.value}
                            onChange={field.onChange}
                            type='number'
                            error={Boolean(fieldState.error)}
                            hintText={fieldState.error?.message}
                        />
                    )}
                />
            </form>
        </StanModal>
    );
}
